import type {
  CandidateResponse,
  CandidateWriteRequest,
  CorrectionResponse,
  CreateEmployeeRequest,
  CreatePayrollRunRequest,
  EmployeePayResponse,
  EmployeeResponse,
  JobRoleResponse,
  JobRoleWriteRequest,
  LeaveBalanceResponse,
  LeaveTypeResponse,
  LeaveTypeUpdateRequest,
  PayrollRunResponse,
  RequisitionResponse,
  RequisitionWriteRequest,
  ShiftSchema,
  SubmitCorrectionRequest,
  SubmitLeaveRequest,
  SurfacePersonResponse,
  UpdateEmployeePayRequest,
  UpdateEmployeeRequest,
  UpdatePayrollRunRequest,
} from '@palaccount/contracts';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  MAX_PICKER_IDS,
  PICKER_PAGE_SIZE,
  pickerIds,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

type ShiftResponse = z.infer<typeof ShiftSchema>;

/** The Employees & HR data layer — same shape as banking/partners. */

export const HR_KEY = ['hr'] as const;

// ── HR-01: employees ────────────────────────────────────────────────────────

export interface EmployeeListFilters {
  q?: string | undefined;
  department?: EmployeeResponse['department'] | undefined;
  includeTerminated?: boolean | undefined;
}

function employeeQuery(filters: EmployeeListFilters) {
  return {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.department ? { department: filters.department } : {}),
    ...(filters.includeTerminated ? { includeTerminated: 'true' as const } : {}),
  };
}

/**
 * HR-01's list SCREEN — one page at a time (NFR-42), with `keepPreviousData`
 * so pressing Next updates the figures without the table blinking back to a
 * skeleton.
 */
export function usePagedEmployees(
  filters: EmployeeListFilters,
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'employees', 'page', filters, page, pageSize],
    queryFn: async () => {
      const response = await api.hr.listEmployees({
        query: { ...employeeQuery(filters), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * THE EMPLOYEE PICKER'S two reads (GEN-50) — HR-04's leave form and HR-05's
 * attendance screen, which PAGINATION.md §5a carried as the last unconverted
 * `<select>` in PART 12.
 */
export function useEmployeeSearch(q: string, filters: EmployeeListFilters = {}) {
  return useQuery({
    queryKey: [...HR_KEY, 'employees', 'search', q, filters],
    queryFn: async (): Promise<EmployeeResponse[]> => {
      const response = await api.hr.listEmployees({
        query: { ...employeeQuery({ ...filters, q: q || undefined }), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.employees;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The people a form already holds. NOT narrowed by the terminated flag: a
 * leave request or an attendance correction belonging to somebody who has
 * since left must still name them (BR-HR-01-13 is about who a picker OFFERS).
 */
export function useEmployeesHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...HR_KEY, 'employees', 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<EmployeeResponse[]> => {
      const response = await api.hr.listEmployees({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.employees;
    },
  });
}

/**
 * BR-HR-01-05d's feed for ONE surface dropdown: active employees carrying an
 * ACTIVE job role that unlocks it, queried live. The quotation's Sales rep
 * picker (BR-SAL-01-15) reads `surface: 'sales_rep'`. The endpoint is
 * hr.view; a caller whose role cannot read it gets an error and the calling
 * screen degrades to its stored value — never a guessed list.
 */
export function useSurfacePeople(
  surface: 'sales_rep' | 'warehouse_manager' | 'approver' | 'cashier' | 'accountant' | 'driver',
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...HR_KEY, 'people', surface],
    enabled: opts.enabled ?? true,
    retry: false,
    queryFn: async (): Promise<SurfacePersonResponse[]> => {
      const response = await api.hr.listSurfacePeople({ query: { surface } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.people;
    },
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateEmployeeRequest): Promise<EmployeeResponse> => {
      const response = await api.hr.createEmployee({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; body: UpdateEmployeeRequest }) => {
      const response = await api.hr.updateEmployee({
        params: { employeeId: input.employeeId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useTerminateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; terminationDate: string }) => {
      const response = await api.hr.terminateEmployee({
        params: { employeeId: input.employeeId },
        body: { terminationDate: input.terminationDate },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

/**
 * R15: the pay profile is its own payroll.view-gated read. A 403 here means
 * the pay card simply does not render — the tab-level fence.
 */
export function useEmployeePay(employeeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...HR_KEY, 'pay', employeeId],
    enabled: enabled && employeeId !== null,
    retry: false,
    queryFn: async (): Promise<EmployeePayResponse> => {
      const response = await api.hr.getEmployeePay({ params: { employeeId: employeeId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useUpdateEmployeePay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; body: UpdateEmployeePayRequest }) => {
      const response = await api.hr.updateEmployeePay({
        params: { employeeId: input.employeeId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

// ── job roles (R11) ─────────────────────────────────────────────────────────

export function useJobRoles(includeInactive = false) {
  return useQuery({
    queryKey: [...HR_KEY, 'jobRoles', includeInactive],
    queryFn: async (): Promise<JobRoleResponse[]> => {
      const response = await api.hr.listJobRoles({
        query: includeInactive ? { includeInactive: 'true' } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.jobRoles;
    },
  });
}

export function useCreateJobRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: JobRoleWriteRequest) => {
      const response = await api.hr.createJobRole({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useUpdateJobRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { jobRoleId: string; body: Partial<JobRoleWriteRequest> }) => {
      const response = await api.hr.updateJobRole({
        params: { jobRoleId: input.jobRoleId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

// ── HR-03: attendance ───────────────────────────────────────────────────────

export function useMyDay() {
  return useQuery({
    queryKey: [...HR_KEY, 'myDay'],
    queryFn: async () => {
      const response = await api.attendance.myDay();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.attendance.checkIn({ body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useCheckOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.attendance.checkOut({ body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

/**
 * HR-03 §5d's register — the MONTH was always a window, and now the ROWS are
 * a page too (NFR-42): one row per employee, each carrying thirty-one derived
 * squares.
 */
export function useRegister(month: string, enabled = true, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...HR_KEY, 'register', month, page, pageSize],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await api.attendance.register({ query: { month, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** §5e's monthly summary — paged on the same rows as the register. */
export function useAttendanceSummary(
  month: string,
  enabled = true,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'summary', month, page, pageSize],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await api.attendance.summary({ query: { month, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * HR-03's Needs-attention queue, one page at a time (NFR-42) — both filters
 * applied by the SERVER, the month included.
 */
export function useCorrections(
  filters: { status?: CorrectionResponse['status']; month?: string } = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'corrections', filters, page, pageSize],
    retry: false,
    queryFn: async () => {
      const response = await api.attendance.listCorrections({
        query: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.month ? { month: filters.month } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSubmitCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitCorrectionRequest) => {
      const response = await api.attendance.submitCorrection({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useDecideCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { correctionId: string; approve: boolean; comment?: string }) => {
      if (input.approve) {
        const response = await api.attendance.approveCorrection({
          params: { correctionId: input.correctionId },
          body: {},
        });
        if (response.status !== 200) throwApiError(response.status, response.body);
        return response.body;
      }
      const response = await api.attendance.rejectCorrection({
        params: { correctionId: input.correctionId },
        body: { comment: input.comment ?? '' },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useShifts() {
  return useQuery({
    queryKey: [...HR_KEY, 'shifts'],
    retry: false,
    queryFn: async (): Promise<ShiftResponse[]> => {
      const response = await api.attendance.listShifts();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.shifts;
    },
  });
}

export function useAssignShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeIds: string[]; shiftId: string }) => {
      const response = await api.attendance.assignShift({ body: input });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

// ── HR-04: leave ────────────────────────────────────────────────────────────

export function useLeaveTypes() {
  return useQuery({
    queryKey: [...HR_KEY, 'leaveTypes'],
    queryFn: async (): Promise<LeaveTypeResponse[]> => {
      const response = await api.leave.listTypes();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.types;
    },
  });
}

export function useUpdateLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leaveTypeId: string; body: LeaveTypeUpdateRequest }) => {
      const response = await api.leave.updateType({
        params: { leaveTypeId: input.leaveTypeId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useMyBalances() {
  return useQuery({
    queryKey: [...HR_KEY, 'myBalances'],
    retry: false,
    queryFn: async (): Promise<LeaveBalanceResponse[]> => {
      const response = await api.leave.myBalances();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.balances;
    },
  });
}

/**
 * HR-04's Balances tab, one page of PEOPLE at a time (NFR-42) — the response
 * was 13.7 MB at 5,000 employees, because every row carries a figure for every
 * active type (BR-HR-04-19).
 *
 * `types` still arrives whole: it is the column headings, and a table that
 * changed shape as it was paged would be a different table on every page.
 */
export function useAllBalances(
  enabled: boolean,
  page: number,
  pageSize: number,
  q: string,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'balances', page, pageSize, q],
    enabled,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await api.leave.balances({
        query: { page, pageSize, ...(q.trim() ? { q: q.trim() } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * HR-04's requests, one page at a time (NFR-42). `scope` is not a filter the
 * caller chooses — the server resolves it from the reader's own position and
 * NAMES what it answered with (BR-HR-04-13/-18) — so the response's own
 * `scope` is what a screen should label its list from.
 */
export function useLeaveRequests(
  scope: 'mine' | 'approvals' | 'all',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'leaveRequests', scope, page, pageSize],
    retry: false,
    queryFn: async () => {
      const response = await api.leave.listRequests({ query: { scope, page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSubmitLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitLeaveRequest) => {
      const response = await api.leave.submit({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useCancelLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const response = await api.leave.cancel({ params: { requestId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useDecideLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { requestId: string; approve: boolean; comment?: string }) => {
      if (input.approve) {
        const response = await api.leave.approve({ params: { requestId: input.requestId }, body: {} });
        if (response.status !== 200) throwApiError(response.status, response.body);
        return response.body;
      }
      const response = await api.leave.reject({
        params: { requestId: input.requestId },
        body: { comment: input.comment ?? '' },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useLeaveCalendar(month: string, enabled: boolean) {
  return useQuery({
    queryKey: [...HR_KEY, 'leaveCalendar', month],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await api.leave.calendar({ query: { month } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

// ── HR-02: payroll ──────────────────────────────────────────────────────────

/** HR-02's register of past runs, one page at a time (NFR-42). */
export function usePayrollRuns(enabled: boolean, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...HR_KEY, 'payrollRuns', page, pageSize],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await api.payroll.list({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePayrollRun(runId: string | null) {
  return useQuery({
    queryKey: [...HR_KEY, 'payrollRun', runId],
    enabled: runId !== null,
    retry: false,
    queryFn: async (): Promise<PayrollRunResponse> => {
      const response = await api.payroll.get({ params: { runId: runId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function usePayrollApprovers(enabled: boolean) {
  return useQuery({
    queryKey: [...HR_KEY, 'payrollApprovers'],
    enabled,
    retry: false,
    queryFn: async () => {
      const response = await api.payroll.approvers();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.approvers;
    },
  });
}

export function useCreatePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePayrollRunRequest): Promise<PayrollRunResponse> => {
      const response = await api.payroll.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useUpdatePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { runId: string; body: UpdatePayrollRunRequest }) => {
      const response = await api.payroll.update({ params: { runId: input.runId }, body: input.body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function usePayrollAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      runId: string;
      action: 'submit' | 'approve' | 'post' | 'cancel';
    }) => {
      const params = { params: { runId: input.runId }, body: {} };
      const response =
        input.action === 'submit'
          ? await api.payroll.submit(params)
          : input.action === 'approve'
            ? await api.payroll.approve(params)
            : input.action === 'post'
              ? await api.payroll.post(params)
              : await api.payroll.cancel(params);
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useRejectPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { runId: string; reason: string }) => {
      const response = await api.payroll.reject({
        params: { runId: input.runId },
        body: { reason: input.reason },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useGeneratePayslips() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      const response = await api.payroll.payslips({ params: { runId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

/**
 * HR-02 §5.9's payslip batch, one page at a time (NFR-42) — one row per
 * INCLUDED employee, so it is headcount-sized. Returns the whole body, rows
 * and meta together, so the `<Pager>` and the card list cannot disagree.
 */
export function usePayslips(
  runId: string | null,
  generated: boolean,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'payslips', runId, page, pageSize],
    enabled: runId !== null && generated,
    retry: false,
    queryFn: async () => {
      const response = await api.payroll.getPayslips({
        params: { runId: runId! },
        query: { page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

// ── HR-05: recruitment ──────────────────────────────────────────────────────

/** HR-05's requisition list, one page at a time (NFR-42). */
export function useRequisitions(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...HR_KEY, 'requisitions', page, pageSize],
    retry: false,
    queryFn: async () => {
      const response = await api.recruitment.listRequisitions({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The "Applied for" PICKER's own read — §5's own compromise, and it is worth
 * naming rather than hiding.
 *
 * The list screen above is paged; this `<select>` is not a list screen, and
 * paging the route underneath a picker is PAGINATION.md §3 step 8's defect
 * exactly: a requisition past the cut cannot be chosen and nothing on screen
 * says so. So it asks for `MAX_PAGE_SIZE` rather than the unbounded `'all'`,
 * which is the same answer the LIST FILTERS wave gave three other pickers —
 * bounded at 500 where it was unbounded, and it does NOT add an entry to
 * `UNPAGED_CALLERS`, which may only shrink.
 *
 * That is BETTER, NOT FIXED, and §5a already says so: 500 is still a silent
 * cut. What removes it is GEN-50's typeahead — the same `q`/`ids` pair the
 * item and partner pickers now use — and this hook is where that lands. It is
 * left for a wave that can carry the whole picker, because a half-converted
 * picker is worse than the bounded one.
 */
export function useRequisitionOptions() {
  return useQuery({
    queryKey: [...HR_KEY, 'requisitions', 'options'],
    retry: false,
    queryFn: async (): Promise<RequisitionResponse[]> => {
      const response = await api.recruitment.listRequisitions({
        query: { page: 1, pageSize: MAX_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.requisitions;
    },
  });
}

export function useCreateRequisition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RequisitionWriteRequest) => {
      const response = await api.recruitment.createRequisition({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useTransitionRequisition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      requisitionId: string;
      to: 'requested' | 'approved' | 'open' | 'on_hold' | 'closed';
    }) => {
      const response = await api.recruitment.transitionRequisition({
        params: { requisitionId: input.requisitionId },
        body: { to: input.to },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

/**
 * HR-05's pipeline, one page at a time (NFR-42) — all three filters applied
 * by the SERVER, so §7's board is one paged read per stage column rather than
 * one read of every candidate the tenant has ever had.
 */
/**
 * The candidate a screen is already holding, resolved by id. HR-05's board is
 * one paged read per stage column, so the detail card cannot look its row up
 * in an array of everybody — and it has to keep that row when the board's own
 * Move action sends it to a different column.
 */
export function useCandidatesHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...HR_KEY, 'candidates', 'held', key],
    enabled: key.length > 0,
    /*
     * DO NOT ADD `placeholderData: keepPreviousData` HERE, however much the
     * flicker on selection invites it — and three of its neighbours in this
     * file carry exactly that option, so the invitation is a strong one.
     *
     * ERR-HR-05-05 ("the offer card shows whatever was last typed, not their
     * offer") is prevented by this hook going briefly `undefined` when the
     * selected id changes: `selected` becomes null, `CandidateDetail`
     * UNMOUNTS, and its `useState(candidate.offerSalary ?? '')` initialisers
     * run again against the new candidate. Keeping the previous data keeps
     * the component mounted, and the offer boxes then carry the last
     * candidate's figures — the prototype defect, verbatim.
     *
     * Verified by mutation, not by argument: adding the option makes
     * `apps/web/src/test/err-clusters-2-hr05.test.tsx` fail with
     * `expected '4200.00' to be ''`. If the flicker must go, give
     * CandidateDetail a `key={candidate.id}` FIRST and re-run that file.
     */
    retry: false,
    queryFn: async (): Promise<CandidateResponse[]> => {
      const response = await api.recruitment.listCandidates({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.candidates;
    },
  });
}

export function useCandidates(
  filters: {
    stage?: CandidateResponse['stage'];
    requisitionId?: string;
    source?: CandidateResponse['source'];
  } = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...HR_KEY, 'candidates', filters, page, pageSize],
    retry: false,
    queryFn: async () => {
      const response = await api.recruitment.listCandidates({
        query: {
          ...(filters.stage ? { stage: filters.stage } : {}),
          ...(filters.requisitionId ? { requisitionId: filters.requisitionId } : {}),
          ...(filters.source ? { source: filters.source } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useCreateCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CandidateWriteRequest) => {
      const response = await api.recruitment.createCandidate({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useAdvanceCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (candidateId: string) => {
      const response = await api.recruitment.moveCandidate({
        params: { candidateId },
        body: {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useRejectCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      candidateId: string;
      reason: NonNullable<CandidateResponse['rejectionReason']>;
      talentPool: boolean;
    }) => {
      const response = await api.recruitment.rejectCandidate({
        params: { candidateId: input.candidateId },
        body: { reason: input.reason, talentPool: input.talentPool },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

/**
 * ERR-HR-05-06 — "rejected somebody and later wanted them kept on file". The
 * §14 row's own advice is "the talent-pool switch is on the candidate record
 * as well as the reject panel — SET IT ON THE CANDIDATE", and this hook is the
 * half that was missing: the route has existed since the recruitment service
 * was written and nothing on the web called it.
 *
 * `useRejectCandidate` above carries `talentPool` too, but it cannot answer
 * this row — it is reachable only from the reject panel, which is drawn for
 * ACTIVE candidates. Once somebody is rejected that panel is gone, so the
 * decision was effectively frozen at the moment of rejection. This route is
 * the one that changes it afterwards (BR-HR-05-11/-17: "changeable AFTER
 * rejection — the prototype could not").
 *
 * The server refuses it for a candidate who is not rejected, so the screen
 * offers it in exactly that state rather than relying on the refusal.
 */
export function useSetTalentPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { candidateId: string; talentPool: boolean }) => {
      const response = await api.recruitment.setTalentPool({
        params: { candidateId: input.candidateId },
        body: { talentPool: input.talentPool },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useAddScorecard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      candidateId: string;
      round: 'phone_screen' | 'technical' | 'panel' | 'final';
      rating: number;
      notes?: string;
    }) => {
      const response = await api.recruitment.addScorecard({
        params: { candidateId: input.candidateId },
        body: {
          round: input.round,
          rating: input.rating,
          ...(input.notes !== undefined ? { notes: input.notes } : {}),
        },
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useSaveOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      candidateId: string;
      salary: string;
      startDate: string;
      status: 'sent' | 'accepted' | 'declined' | 'negotiating';
    }) => {
      const response = await api.recruitment.saveOffer({
        params: { candidateId: input.candidateId },
        body: { salary: input.salary, startDate: input.startDate, status: input.status },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useHireCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (candidateId: string) => {
      const response = await api.recruitment.hire({ params: { candidateId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}

export function useScheduleInterview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (candidateId: string) => {
      const response = await api.recruitment.scheduleInterview({
        params: { candidateId },
        body: {},
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: HR_KEY }),
  });
}
